export type AnnouncementCategoryType = "education" | "system";

export interface ClientAnnouncement {
  id: string;
  imageUrl?: string;
  /** API'den gelen görsel dosya ID (FileImage ile gösterilir) */
  imageFileID?: string | null;
  date: string;
  category: string;
  categoryType: AnnouncementCategoryType;
  title: string;
  description: string;
}

// --- API types (/api/client/announcements) ---

/** Category item from GET /api/client/announcements/categories */
export interface ClientAnnouncementCategory {
  id: string;
  name: string;
  type: string; // e.g. "department" | "education"
}

/** Inner body from GET /api/client/announcements/categories */
export interface ClientAnnouncementsCategoriesPayload {
  success: boolean;
  status: number;
  data: { items: ClientAnnouncementCategory[] };
  message?: string;
}

/** Full response.data: { status, data: ClientAnnouncementsCategoriesPayload } */
export interface ClientAnnouncementsCategoriesResponse {
  status: number;
  data: ClientAnnouncementsCategoriesPayload;
}

/** List item from GET /api/client/announcements (actual API shape) */
export interface ClientAnnouncementListItem {
  id: string;
  imageFileID: string | null;
  imageUrl?: string | null;
  publishedDate: string; // ISO
  title: string;
  descriptionSnippet: string;
  tags: string[];
}

/** Backend `AnnouncementDisplayType` aynası: duyuru nerede gösterilecek. */
export const ANNOUNCEMENT_DISPLAY_TYPE = {
  HOMEPAGE: 0,
  POPUP: 1,
} as const;

export interface ClientAnnouncementsListParams {
  type?: "general" | "private" | "all";
  /**
   * Gösterim türü filtresi. Verilmezse tür ayrımı yapılmaz — duyurular sayfası
   * hem anasayfa hem popup duyurularını listeler.
   */
  displayType?: 0 | 1;
  search?: string;
  dateFrom?: string; // ISO 8601
  dateTo?: string; // ISO 8601
  sortBy?: "createdAt" | "startDate" | "title";
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
}

/** Inner payload from list endpoint: { success, status, data: [], message, count } */
export interface ClientAnnouncementsListPayload {
  success: boolean;
  status: number;
  data: ClientAnnouncementListItem[];
  message?: string;
  count: number;
}

/** Full axios response.data: { status, data: ClientAnnouncementsListPayload } */
export interface ClientAnnouncementsListResponse {
  status: number;
  data: ClientAnnouncementsListPayload;
}

/** Detail from GET /api/client/announcements/{id} - may include full description */
export interface ClientAnnouncementDetailItem
  extends ClientAnnouncementListItem {
  description?: string;
}

export interface ClientAnnouncementDetailResponse {
  success: boolean;
  status: number;
  data: ClientAnnouncementDetailItem;
  message?: string;
}

/** Map API list/detail item to ClientAnnouncement for UI */
export function mapAnnouncementListItemToClient(
  item: ClientAnnouncementListItem | ClientAnnouncementDetailItem,
): ClientAnnouncement {
  let date = "";
  if (item.publishedDate) {
    try {
      date = new Date(item.publishedDate).toLocaleDateString("tr-TR");
    } catch {
      date = item.publishedDate;
    }
  }
  const description =
    "description" in item && item.description != null
      ? item.description
      : (item.descriptionSnippet ?? "");
  const category = item.tags?.[0] || "Genel";
  const categoryType: AnnouncementCategoryType = category
    .toLowerCase()
    .includes("eğitim")
    ? "education"
    : "system";
  return {
    id: item.id,
    imageUrl:
      typeof item.imageUrl === "string" && item.imageUrl.trim() !== ""
        ? item.imageUrl
        : undefined,
    imageFileID: item.imageFileID ?? null,
    date,
    category,
    categoryType,
    title: item.title,
    description,
  };
}
